/**
 * Browser push via Firebase Cloud Messaging (Fase 5, ADR-0013). The FCM token of this browser is
 * stored in the API's devices table (platform "web"), next to Android tokens, so the existing
 * NotificationService reaches both.
 *
 * Consent (LGPD) is per account and per browser: pushes only go to this browser after the user
 * clicked "Ativar notificações" while logged into that account, even if the browser permission
 * was already granted to someone else before. Firebase is lazy-loaded, never in the main bundle.
 */
import { devicesApi } from '../../lib/api/devices';
import { firebaseConfig, firebaseVapidKey, isPushConfigured } from '../../lib/firebase-config';
import { currentInstallPlatform } from '../install/install-platform';

export type PushStatus =
  | 'unconfigured' // no Firebase config in this build: hide everything
  | 'unsupported' // browser without web push
  | 'needs-install' // iPhone/iPad: web push only works from the installed app (iOS 16.4+)
  | 'denied' // the user blocked notifications for this site in the browser
  | 'default' // can be enabled
  | 'enabled';

type StoredRegistration = { userId: number; deviceId: number };

const STORAGE_KEY = 'licitavia.push';

function readStored(): StoredRegistration | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredRegistration) : null;
  } catch {
    return null;
  }
}

function writeStored(value: StoredRegistration | null) {
  try {
    if (value) localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Private mode / blocked storage: push still works this session; the next visit asks again.
  }
}

async function loadMessaging() {
  const [{ getApp, getApps, initializeApp }, messagingModule] = await Promise.all([
    import('firebase/app'),
    import('firebase/messaging'),
  ]);
  const app = getApps().length > 0 ? getApp() : initializeApp(firebaseConfig!);
  return { ...messagingModule, messaging: messagingModule.getMessaging(app) };
}

export async function getPushStatus(userId: number): Promise<PushStatus> {
  if (!isPushConfigured) return 'unconfigured';

  const hasWebPush = 'serviceWorker' in navigator && 'Notification' in window && 'PushManager' in window;
  if (!hasWebPush) return currentInstallPlatform() === 'ios' ? 'needs-install' : 'unsupported';

  const { isSupported } = await import('firebase/messaging');
  if (!(await isSupported())) return 'unsupported';

  if (Notification.permission === 'denied') return 'denied';
  if (Notification.permission === 'granted' && readStored()?.userId === userId) return 'enabled';
  return 'default';
}

async function registerThisBrowser(userId: number): Promise<void> {
  // Only exists in a production build (vite build/preview): the SW is not registered by `vite dev`.
  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration) throw new Error('Service worker não registrado.');

  const { getToken, messaging } = await loadMessaging();
  const token = await getToken(messaging, { vapidKey: firebaseVapidKey, serviceWorkerRegistration: registration });
  const { data } = await devicesApi.register(token);
  writeStored({ userId, deviceId: data.id });
}

/** Must be called from a click: browsers only show the permission prompt on a user gesture. */
export async function enablePush(userId: number): Promise<PushStatus> {
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'default';

  await registerThisBrowser(userId);
  return 'enabled';
}

/**
 * Re-sends the current token once per session (FCM rotates tokens; this also refreshes
 * last_seen_at). No-op unless this account already opted in on this browser.
 */
export async function syncPush(userId: number): Promise<void> {
  if ((await getPushStatus(userId)) !== 'enabled') return;
  await registerThisBrowser(userId);
}

/** Stops pushes to this browser: on "Desativar" and on logout (while the session still exists). */
export async function disablePush(): Promise<void> {
  const stored = readStored();
  writeStored(null);
  if (!stored) return;

  await devicesApi.remove(stored.deviceId).catch(() => undefined);
  if (isPushConfigured) {
    await loadMessaging()
      .then(({ deleteToken, messaging }) => deleteToken(messaging))
      .catch(() => undefined);
  }
}

/**
 * While the dashboard is the visible window, FCM hands the message to the page instead of
 * showing it; show it anyway (the user may be on another screen) and let the caller refresh data.
 */
export async function listenForegroundPushes(onPush: () => void): Promise<() => void> {
  const { onMessage, messaging } = await loadMessaging();
  return onMessage(messaging, async (payload) => {
    onPush();
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration || Notification.permission !== 'granted') return;
    await registration.showNotification(payload.notification?.title ?? 'Licitavia', {
      body: payload.notification?.body,
      icon: '/app-icons/pwa-192x192.png',
      // Opened by the notificationclick handler in sw.ts.
      data: { link: payload.fcmOptions?.link ?? payload.data?.link ?? '/notificacoes' },
    });
  });
}
